<template>
  <header qaid="nav-bar" class="nav-bar relative top-0 h-[var(--header-height)]">
    <nav
      ref="navigation"
      :class="[
        `active--tier-${currentTier - 1}`,
        {'mobile-view': isCompactView, 'mobile-open': isMobileNavOpen, electra: isElectra},
      ]"
      class="main-nav w-full grid grid-cols-12 z-50 top-0 left-0 right-0 fixed h-[var(--header-height)] lg:h-auto"
    >
      <div
        ref="burger"
        class="row-start-1 col-start-1 grid-cols-2 col-span-3 text-white px-2 flex items-center bg-[var(--nav-mobile-top-bar-bg)] z-20 translate-y-0 will-change-transform lg:hidden"
      >
        <button
          v-if="isCompactView"
          :class="{active: isMobileNavOpen}"
          class="h-6 w-6 grid place-content-center"
          :aria-expanded="isMobileNavOpen"
          aria-controls="navigation"
          tabindex="0"
          @click.prevent="toggleMobileNav"
          @keydown.prevent.enter="toggleMobileNav"
          @keydown.prevent.down="toggleMobileNav"
        >
          <span class="sr-only" v-text="$t('text.button.menu')" /><span class="burger" />
        </button>
      </div>
      <div
        id="navigation"
        class="nav-wrapper grid-rows-[auto,_auto] lg:grid-rows-subgrid grid-cols-subgrid grid col-span-12 row-span-2 lg:row-start-1 row-start-2 col-start-1"
        :class="!isCompactView || isMobileNavOpen ? 'pointer-events-auto' : 'pointer-events-none'"
      >
        <div
          class="main-nav-container block lg:grid grid-cols-subgrid grid-rows-subgrid col-span-12 row-span-2 w-full max-w-screen overflow-y-auto overflow-x-hidden overscroll-contain"
          style="max-height: calc(100dvh - 60px)"
          qaid="nav-container"
        >
          <nav-brand-logos
            v-if="!isB2b && !isCompactView"
            :brand-urls="brandUrls"
            :is-electra-supported-site="isElectraSupportedSite"
            :is-trek-travel-locale="isTrekTravelLocale"
            :is-diamant-locale="isDiamantLocale"
          />
          <nav-utilities-b2c v-if="!isB2b && !isCompactView">
            <template #minicart>
              <slot name="minicart" />
            </template>
            <template #language-country-info>
              <slot name="language-country-info" />
            </template>
            <template #profile-login>
              <slot name="profile-login" />
            </template>
          </nav-utilities-b2c>
          <div
            v-if="!isCompactView"
            class="nav-logo relative text-white col-span-6 row-start-1 translate-y-0 will-change-transform col-start-4 lg:col-span-2 lg:row-start-2 lg:col-start-1 px-2 z-20 flex justify-center items-center lg:justify-start bg-[var(--nav-mobile-top-bar-bg)] lg:bg-transparent"
          >
            <a
              :href="`https://${hostUrl}${encodedContextPath}`"
              class="lg:absolute top-0 left-0 bottom-0 bg-[var(--nav-mobile-top-bar-bg)] px-2 grid place-content-center"
              tab-index="0"
              :qaid="logoQaid"
            >
              <span class="sr-only">{{ $t(logoLinkText) }}</span>
              <svg v-if="!isElectra" width="116" height="14" viewBox="0 0 116 14">
                <title>{{ $t('title.trek.bikes') }}</title>
                <path
                  fill="#fff"
                  fill-rule="evenodd"
                  d="m95.362 0-4.965 13.72h-9.672L85.69 0h9.67ZM115.2 0l-11.118 6.464 6.422 7.256H99.34l-5.742-7.333L103.87 0h11.331ZM83.57 0l-.951 2.626H67.273l-1.04 2.858h15.347l-.95 2.625H65.294l-.628 1.73c-.227.63.175 1.267 1.022 1.267H79.55l-.948 2.614H58.767c-.801 0-1.277-.364-1.679-.813a474.501 474.501 0 0 1-1.484-1.679c-.454-.516-.413-1.201-.197-1.804L58.809 0h24.76ZM54.183 0c1.376 0 2.162.82 1.677 2.19-.295.836-1.2 3.38-1.607 4.408-.36.902-1.133 1.636-2.51 1.636H51.022l-.814-.002h-.744l-.156-.001-.49-.001 4.877 5.49H42.846l-5.73-7.324.969-.001h.228l1.056-.001h.29l1.193-.002h1.662l1.19-.002c.82 0 1.392-.433 1.628-1.005.175-.423.417-1.095.59-1.66.171-.553-.128-1.1-.88-1.1h-7.03L34.02 13.72h-9.422L29.569 0h24.614ZM27.851 0 26.9 2.626h-8.488L14.395 13.72H5.264L9.277 2.626H0L.95 0h26.901Z"
                  clip-rule="evenodd"
                />
              </svg>
              <svg v-else class="electra" viewBox="0 0 158 32">
                <title>{{ $t('title.electra.bikes') }}</title>
                <path
                  fill="#fff"
                  d="M21.55 6.788c3.416-.244 14.253-2.183 17.472.424-.997-3.7-7.224-4.66-8.204-4.742-.573-.049-2.779-.293-6.702-.228.017-.13.066-1.19-.915-1.5 0 0-.784-.276-1.291-.08-.098.032-1.03.781-2.174 1.759-2.01.13-4.282.326-6.848.62-.98.113-2.68 1.189-1.994 2.313.654 1.06 1.83 1.532 3.84 1.662-1.34 1.45-4.805 4.97-7.812 8.555-1.324.13-2.713.277-4.2.473-1.7.212-2.975 1.482-1.7 2.623.13.114.588.7 2.795.945-3.874 5.606-3.661 8.457-1.815 10.43 1.243 1.335 5.1 1.775 9.676 1.01 3.122-.522 12.994-2.852 14.04-4.172.507-.636.72-1.369-.8-1.287 0 0-5.116.945-7.634 1.4-3.121.571-11.653 2.478-11.408-1.156.082-1.32 1.16-3.406 3.596-6.388 4.756-.554 15.92-1.76 19.907-1.075 1.079.18 2.24.635 2.615.945-.163-1.287-3.072-2.721-4.625-3.112-1.177-.294-4.822-1.434-13.73-1.092 2.632-2.607 5.738-5.67 7.911-8.327ZM148.088 27.043c-2.55 1.091-7.453.619 1.405-11.097.915-1.206 1.569-2.265 1.569-3.21-1.683-.228-2.092.26-3.857-.033-.147.554-.049 1.125-.834 1.613 0 0-.163-.7-.523-1.27-.441-.718-2.533-3.602-10.493-.343-2.925 1.353-10.329 6.274-11.931 13.101-1.128.717-2.305 1.222-3.416 1.337-6.75.668-5.067-5.818 5.753-12.19 0 0 .572-.374 1.144-.847 1.063-.863-3.808-2.118-10.166-.537-2.174.537-2.975-.733-2.664-1.76.278-.88 1.308-1.418 1.308-1.418s-6.98-.016-7.535 4.009c-.164 1.222.212 2.102.948 2.656-2.288 3.503-10.69 11.846-14.138 10.429-1.193-.49-.703-2.38 1.03-4.628 2.402-3.112 8.221-9.68 12.519-14.34 2.24-.146 4.054-.293 4.806-.342 4.641-.293 15.968-2.33 21.721.978-4.249-2.689-7.289-3.536-11.457-4.14-3.874-.553-6.227-.814-11.278-.553.442-.456.801-.799 1.03-.994.883-1.043.899-2.086.36-2.33-1.324-.587-3.384-1.043-3.727-.945-.343.081-1.683 1.971-2.206 2.476-.197.196-1.112 1.141-2.174 2.282-.294.032-.572.049-.883.081-1.307.114-5.1.636-10.853.489-1.323-.033-2.647-.424-3.072-.196-.425.229-1.896 1.5-.115 2.608 1.766 1.108 6.277 1.222 9.251 1.091.638-.032 1.308-.065 1.994-.097-.229.244-.376.423-.408.489-.262.407-6.146 7.723-8.924 12.384-8.532 7.512-17.26 6.338-17.865 3.83-.294-1.207-.049-3.814 4.152-7.692 4.2-3.895 8.875-4.612 9.806-4.449.769.13.621.538.458.978-.376 1.027-2.55 2.917-3.007 3.373-.948.978-.9 2.037 1.88 2.591-.769-.831.048-1.32 1.225-2.232 3.383-2.852 3.58-3.52 3.825-4.58.36-1.482-1.08-3.177-4.332-3.242-6.521-.13-12.797 4.563-16.85 9.125-1.848 2.07-2.632 3.748-2.894 5.068-.784.391-1.732.798-2.566 1.075-15.74 5.492-10.95-4.139-10.95-4.139 1.127.701 3.66 1.125 5.295.946 7.96-.766 12.781-6.812 11.997-10.25-.572-2.526-3.808-3.194-8.303-1.89-7.91 2.444-13.68 9.549-14.269 14.616-1.765 1.255-4.462 2.917-6.995 3.227-1.291.163-6.08.521-5.165-6.583 0 0 24.664-7.447 20.79-19.294 0 0-.735-1.792-3.497-1.531-4.103.39-9.366 3.259-14.94 8.766-3.399 3.357-6.537 7.317-7.518 13.004 0 0-4.707 2.688-3.759 4.823.115-.684.687-1.303 1.716-2.02.621-.424 1.52-.783 1.7-.799-.196 2.249 2.043 7.137 7.845 6.371 3.596-.472 6.734-1.857 9.84-4.269.425 2.803 2.974 4.514 8.433 3.503 5.296-.994 8.843-3.063 10.297-4.073.066.586.246 1.059.425 1.417.72 1.45 2.485 3.21 6.832 3.015 6.522-.293 11.72-3.21 14.939-5.687-.654 2.118-.556 4.318.785 5.214 2.925 1.94 11.097.082 19.76-12.253 1.879.049 4.298-.554 6.734-1.777-9.039 7.3-6.636 14.096-2.468 14.52 2.811.276 5.328-1.19 7.583-3.358-.016 1.988 1.03 3.325 2.305 3.602 1.536.326 5.443.212 13.419-6.143.457-.294.703-.229.523.619-1.03 5.72 5.524 7.805 9.692 3.405 1.095-1.157 4.609-5.296 8.63-5.296-4.642-.488-8.352 3.145-9.692 3.716ZM62.934 13.795c5.981-3.308 8.646-1.206 3.89 2.868-6.604 5.654-10.755 3.666-10.624 3.357 1.814-3.797 6.733-6.225 6.733-6.225Zm-24.55 5.703C43.745 7.668 55.235 3.138 56.25 3.301c2.14.342-3.236 10.282-17.652 16.246-.278.114-.213-.049-.213-.049Zm93.964 6.974c-2.664 1.45-4.102.587-3.808-1.808.376-3.145 2.517-4.987 4.985-7.072 5.655-4.775 9.676-3.602 10.15-3.194 2.239 1.89-6.636 9.532-11.327 12.074Z"
                />
              </svg>
            </a>
          </div>
          <ul
            ref="navWrapper"
            class="col-span-12 row-start-1 lg:row-start-2 col-start-1 lg:col-span-8 lg:col-start-3 flex justify-center list-none p-0 m-0 flex-col lg:flex-row max-w-screen"
            qaid="nav-utility-b2c"
            role="menubar"
          >
            <slot name="navbarcontent" />
          </ul>
          <div
            v-if="isB2b && isCompactView"
            class="z-10 col-span-12 row-start-2 col-start-1 justify-start items-stretch"
          >
            <slot name="utilities" />
          </div>
          <nav-brand-logos
            v-if="!isB2b && isCompactView"
            :focus-next-mobile-menu-item="focusNextMobileMenuItem"
            :focus-previous-mobile-menu-item="focusPreviousMobileMenuItem"
            :toggle-mobile-nav="toggleMobileNav"
            :is-mobile-nav-open="isMobileNavOpen"
            :brand-urls="brandUrls"
            :is-electra-supported-site="isElectraSupportedSite"
            :is-trek-travel-locale="isTrekTravelLocale"
            :is-diamant-locale="isDiamantLocale"
          >
            <template #mobile-language>
              <slot
                name="mobile-language"
                :focus-next-mobile-menu-item="focusNextMobileMenuItem"
                :focus-previous-mobile-menu-item="focusPreviousMobileMenuItem"
                :toggle-mobile-nav="toggleMobileNav"
              />
            </template>
            <template #mobile-login>
              <slot
                name="mobile-login"
                :focus-next-mobile-menu-item="focusNextMobileMenuItem"
                :focus-previous-mobile-menu-item="focusPreviousMobileMenuItem"
                :toggle-mobile-nav="toggleMobileNav"
              />
            </template>
          </nav-brand-logos>
        </div>
      </div>
      <div
        v-if="isCompactView"
        class="nav-logo relative text-white col-span-6 row-start-1 translate-y-0 will-change-transform col-start-4 lg:col-span-2 lg:row-start-2 lg:col-start-1 px-2 z-20 flex justify-center items-center lg:justify-start bg-[var(--nav-mobile-top-bar-bg)] lg:bg-transparent"
      >
        <a
          :href="`https://${hostUrl}${encodedContextPath}`"
          class="lg:absolute top-0 left-0 bottom-0 bg-[var(--nav-mobile-top-bar-bg)] px-2 grid place-content-center"
          tab-index="0"
          :qaid="logoQaid"
        >
          <span class="sr-only">{{ $t(logoLinkText) }}</span>
          <svg v-if="!isElectra" width="116" height="14" viewBox="0 0 116 14">
            <title>{{ $t('title.trek.bikes') }}</title>
            <path
              fill="#fff"
              fill-rule="evenodd"
              d="m95.362 0-4.965 13.72h-9.672L85.69 0h9.67ZM115.2 0l-11.118 6.464 6.422 7.256H99.34l-5.742-7.333L103.87 0h11.331ZM83.57 0l-.951 2.626H67.273l-1.04 2.858h15.347l-.95 2.625H65.294l-.628 1.73c-.227.63.175 1.267 1.022 1.267H79.55l-.948 2.614H58.767c-.801 0-1.277-.364-1.679-.813a474.501 474.501 0 0 1-1.484-1.679c-.454-.516-.413-1.201-.197-1.804L58.809 0h24.76ZM54.183 0c1.376 0 2.162.82 1.677 2.19-.295.836-1.2 3.38-1.607 4.408-.36.902-1.133 1.636-2.51 1.636H51.022l-.814-.002h-.744l-.156-.001-.49-.001 4.877 5.49H42.846l-5.73-7.324.969-.001h.228l1.056-.001h.29l1.193-.002h1.662l1.19-.002c.82 0 1.392-.433 1.628-1.005.175-.423.417-1.095.59-1.66.171-.553-.128-1.1-.88-1.1h-7.03L34.02 13.72h-9.422L29.569 0h24.614ZM27.851 0 26.9 2.626h-8.488L14.395 13.72H5.264L9.277 2.626H0L.95 0h26.901Z"
              clip-rule="evenodd"
            />
          </svg>
          <svg v-else class="electra" viewBox="0 0 158 32">
            <title>{{ $t('title.electra.bikes') }}</title>
            <path
              fill="#fff"
              d="M21.55 6.788c3.416-.244 14.253-2.183 17.472.424-.997-3.7-7.224-4.66-8.204-4.742-.573-.049-2.779-.293-6.702-.228.017-.13.066-1.19-.915-1.5 0 0-.784-.276-1.291-.08-.098.032-1.03.781-2.174 1.759-2.01.13-4.282.326-6.848.62-.98.113-2.68 1.189-1.994 2.313.654 1.06 1.83 1.532 3.84 1.662-1.34 1.45-4.805 4.97-7.812 8.555-1.324.13-2.713.277-4.2.473-1.7.212-2.975 1.482-1.7 2.623.13.114.588.7 2.795.945-3.874 5.606-3.661 8.457-1.815 10.43 1.243 1.335 5.1 1.775 9.676 1.01 3.122-.522 12.994-2.852 14.04-4.172.507-.636.72-1.369-.8-1.287 0 0-5.116.945-7.634 1.4-3.121.571-11.653 2.478-11.408-1.156.082-1.32 1.16-3.406 3.596-6.388 4.756-.554 15.92-1.76 19.907-1.075 1.079.18 2.24.635 2.615.945-.163-1.287-3.072-2.721-4.625-3.112-1.177-.294-4.822-1.434-13.73-1.092 2.632-2.607 5.738-5.67 7.911-8.327ZM148.088 27.043c-2.55 1.091-7.453.619 1.405-11.097.915-1.206 1.569-2.265 1.569-3.21-1.683-.228-2.092.26-3.857-.033-.147.554-.049 1.125-.834 1.613 0 0-.163-.7-.523-1.27-.441-.718-2.533-3.602-10.493-.343-2.925 1.353-10.329 6.274-11.931 13.101-1.128.717-2.305 1.222-3.416 1.337-6.75.668-5.067-5.818 5.753-12.19 0 0 .572-.374 1.144-.847 1.063-.863-3.808-2.118-10.166-.537-2.174.537-2.975-.733-2.664-1.76.278-.88 1.308-1.418 1.308-1.418s-6.98-.016-7.535 4.009c-.164 1.222.212 2.102.948 2.656-2.288 3.503-10.69 11.846-14.138 10.429-1.193-.49-.703-2.38 1.03-4.628 2.402-3.112 8.221-9.68 12.519-14.34 2.24-.146 4.054-.293 4.806-.342 4.641-.293 15.968-2.33 21.721.978-4.249-2.689-7.289-3.536-11.457-4.14-3.874-.553-6.227-.814-11.278-.553.442-.456.801-.799 1.03-.994.883-1.043.899-2.086.36-2.33-1.324-.587-3.384-1.043-3.727-.945-.343.081-1.683 1.971-2.206 2.476-.197.196-1.112 1.141-2.174 2.282-.294.032-.572.049-.883.081-1.307.114-5.1.636-10.853.489-1.323-.033-2.647-.424-3.072-.196-.425.229-1.896 1.5-.115 2.608 1.766 1.108 6.277 1.222 9.251 1.091.638-.032 1.308-.065 1.994-.097-.229.244-.376.423-.408.489-.262.407-6.146 7.723-8.924 12.384-8.532 7.512-17.26 6.338-17.865 3.83-.294-1.207-.049-3.814 4.152-7.692 4.2-3.895 8.875-4.612 9.806-4.449.769.13.621.538.458.978-.376 1.027-2.55 2.917-3.007 3.373-.948.978-.9 2.037 1.88 2.591-.769-.831.048-1.32 1.225-2.232 3.383-2.852 3.58-3.52 3.825-4.58.36-1.482-1.08-3.177-4.332-3.242-6.521-.13-12.797 4.563-16.85 9.125-1.848 2.07-2.632 3.748-2.894 5.068-.784.391-1.732.798-2.566 1.075-15.74 5.492-10.95-4.139-10.95-4.139 1.127.701 3.66 1.125 5.295.946 7.96-.766 12.781-6.812 11.997-10.25-.572-2.526-3.808-3.194-8.303-1.89-7.91 2.444-13.68 9.549-14.269 14.616-1.765 1.255-4.462 2.917-6.995 3.227-1.291.163-6.08.521-5.165-6.583 0 0 24.664-7.447 20.79-19.294 0 0-.735-1.792-3.497-1.531-4.103.39-9.366 3.259-14.94 8.766-3.399 3.357-6.537 7.317-7.518 13.004 0 0-4.707 2.688-3.759 4.823.115-.684.687-1.303 1.716-2.02.621-.424 1.52-.783 1.7-.799-.196 2.249 2.043 7.137 7.845 6.371 3.596-.472 6.734-1.857 9.84-4.269.425 2.803 2.974 4.514 8.433 3.503 5.296-.994 8.843-3.063 10.297-4.073.066.586.246 1.059.425 1.417.72 1.45 2.485 3.21 6.832 3.015 6.522-.293 11.72-3.21 14.939-5.687-.654 2.118-.556 4.318.785 5.214 2.925 1.94 11.097.082 19.76-12.253 1.879.049 4.298-.554 6.734-1.777-9.039 7.3-6.636 14.096-2.468 14.52 2.811.276 5.328-1.19 7.583-3.358-.016 1.988 1.03 3.325 2.305 3.602 1.536.326 5.443.212 13.419-6.143.457-.294.703-.229.523.619-1.03 5.72 5.524 7.805 9.692 3.405 1.095-1.157 4.609-5.296 8.63-5.296-4.642-.488-8.352 3.145-9.692 3.716ZM62.934 13.795c5.981-3.308 8.646-1.206 3.89 2.868-6.604 5.654-10.755 3.666-10.624 3.357 1.814-3.797 6.733-6.225 6.733-6.225Zm-24.55 5.703C43.745 7.668 55.235 3.138 56.25 3.301c2.14.342-3.236 10.282-17.652 16.246-.278.114-.213-.049-.213-.049Zm93.964 6.974c-2.664 1.45-4.102.587-3.808-1.808.376-3.145 2.517-4.987 4.985-7.072 5.655-4.775 9.676-3.602 10.15-3.194 2.239 1.89-6.636 9.532-11.327 12.074Z"
            />
          </svg>
        </a>
      </div>
      <nav-utilities-b2c v-if="!isB2b && isCompactView">
        <template #minicart>
          <slot name="minicart" />
        </template>
        <template #language-country-info>
          <slot name="language-country-info" />
        </template>
        <template #profile-login>
          <slot name="profile-login" />
        </template>
      </nav-utilities-b2c>
      <div
        v-if="isB2b && isCompactView"
        class="list-none col-span-3 z-20 col-start-10 lg:col-span-6 row-start-1 lg:col-start-7 flex gap-3 px-2 text-white items-center justify-end bg-[var(--nav-mobile-top-bar-bg)] lg:bg-gray-80 will-change-transform"
      >
        <nav-mobile-search-icon />

        <slot name="cart" />
      </div>
      <div
        v-if="isB2b && !isCompactView"
        class="z-10 col-span-12 row-start-1 col-start-1 flex justify-start items-stretch text-white"
      >
        <slot name="utilities" />
      </div>
      <div
        id="navSearchContainer"
        class="nav__search-container row-start-1 lg:row-start-2 col-start-1 col-span-12 py-0 px-2 m-0 lg:col-start-11 lg:col-span-2 flex items-center justify-end z-50 transition ease transition-duration-200"
      >
        <slot name="search" :on-focused="onFocus" />
      </div>
    </nav>
    <div
      :class="
        (!isCompactView && isNavOpen) || isMobileNavOpen
          ? 'nav-bg-overlay opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none'
      "
      class="transition-opacity ease transition-duration-200 fixed top-0 left-0 right-0 z-30 h-dvh"
    />
    <a
      v-if="!isB2b && language === 'en_US'"
      href="/us/en_US/company/legal_policies/privacy_policy_terms_of_use/#accessibility"
      class="sr-only"
    >
      View our accessibility statement
    </a>
    <slot name="geoprompt" />
  </header>
</template>

<script>
import {computed} from 'vue';
import {mapState, mapActions, mapMutations} from 'vuex';
import NavBrandLogos from '@/navigation/NavBrandLogos.vue';
import NavUtilitiesB2c from '@/navigation/containers/NavUtilitiesB2c.vue';
import NavMobileSearchIcon from '@/navigation/NavMobileSearchIcon.vue';

export default {
  name: 'NavBar',
  components: {
    NavUtilitiesB2c,
    NavBrandLogos,
    NavMobileSearchIcon,
  },
  provide() {
    return {
      isNavOpen: computed(() => this.isNavOpen),
      navDataLoaded: computed(() => this.navDataLoaded),
      isMobileNavOpen: computed(() => this.isMobileNavOpen),
      focusNewTier: computed(() => this.focusNewTier),
      focusNextMenuItem: computed(() => this.focusNextMenuItem),
      focusNextElement: computed(() => this.focusNextElement),
      focusPreviousMenuItem: computed(() => this.focusPreviousMenuItem),
      focusParentTier: computed(() => this.focusParentTier),
      isElectra: computed(() => this.isElectra),
      focusMobileSearchBar: computed(() => this.focusMobileSearchBar),
      isMobileSearchBarExpanded: computed(() => this.isMobileSearchBarExpanded),
      focusNextMobileMenuItem: computed(() => this.focusNextMobileMenuItem),
      focusPreviousMobileMenuItem: computed(() => this.focusPreviousMobileMenuItem),
      toggleMobileNav: computed(() => this.toggleMobileNav),
      isB2b: computed(() => this.isB2b),
      handleNavOccCallCompleted: computed(() => this.handleNavOccCallCompleted),
      currentTier: computed(() => this.currentTier),
    };
  },
  props: {
    brandUrls: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    isB2b: {
      type: Boolean,
      default: false,
    },
    isElectra: {
      type: Boolean,
      default: false,
    },
    isElectraSupportedSite: {
      type: Boolean,
      default: false,
    },
    isTrekTravelLocale: {
      type: Boolean,
      default: false,
    },
    isDiamantLocale: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isNavOpen: false,
      activePane: null,
      currentTier: 0,
      expandedElements: [null, null],
      menuItems: null,
      timeout: false,
      isMobileNavOpen: false,
      isMobileSearchActive: true,
      isMobileSearchBarExpanded: false,
      navSearchInput: null,
      navOccCallsCompleted: 0,
      tierOneNavItemsCount: -1,
      navDataLoaded: false,
    };
  },
  computed: {
    ...mapState('backend', ['language', 'hostUrl', 'encodedContextPath']),
    ...mapState('global', ['isCompactView']),
    ...mapState('viewSize', ['viewBreakpoint']),
    logoLinkText() {
      return this.isElectra ? 'nav.logo.altText.electra' : 'nav.logo.altText';
    },
    searchClasses() {
      if (!this.isCompactView) return;
      const searchClasses = !this.isMobileSearchActive ? '-translate-y-full opacity-0' : 'translate-y-0 opacity-100';
      return searchClasses;
    },
    logoQaid() {
      if (this.isElectra) return 'electra-logo';
      else {
        return 'trek-logo';
      }
    },
  },
  watch: {
    viewBreakpoint: {
      immediate: true,
      handler(val) {
        this.updateViewportState(val);
      },
    },
    isCompactView() {
      this.updateHeaderHeight();
      if (this.navDataLoaded) {
        this.setMenuItems();
      }
      if (!this.isCompactView) {
        if (this.isMobileNavOpen) {
          this.toggleMobileNav();
        }
        if (!this.isNavOpen) {
          this.setActiveTarget(this.menuItems[0], false);
        }
      }
    },
    isNavOpen: {
      handler(open) {
        if (open) {
          document.addEventListener('keydown', this.handleEscape);
          document.addEventListener('click', this.handleClickOutside);
          document.body.classList.add('overflow-hidden');
        } else {
          document.removeEventListener('keydown', this.handleEscape);
          document.removeEventListener('click', this.handleClickOutside);
          document.body.classList.remove('overflow-hidden');
        }
      },
    },
  },
  mounted() {
    this.navSearchInput = this.$refs.navigation.querySelector('.js-site-search-input');
    this.updateHeaderHeight();
    this.countTierOneNavItems();
    document.getElementById('nav-app-fcp')?.classList.add('sr-only');
    window.addEventListener('close-nav-dropdown', this.closeNavDropdown);
  },
  beforeUnmount() {
    window.removeEventListener('close-nav-dropdown', this.closeNavDropdown);
  },
  methods: {
    ...mapActions('global', ['updateViewportState']),
    ...mapMutations('global', ['setHeaderHeight']),

    onFocus(val) {
      this.isMobileSearchBarExpanded = val;
    },

    countTierOneNavItems() {
      this.tierOneNavItemsCount = this.$refs.navWrapper.querySelectorAll('.nav-item__tier-0').length;
    },
    handleNavOccCallCompleted() {
      this.navOccCallsCompleted++;
      if (this.navOccCallsCompleted === this.tierOneNavItemsCount) {
        this.navDataLoaded = true;
        this.setMenuItems();
      }
    },
    resetTabIndices() {
      if (!this.menuItems) return;

      this.menuItems.forEach((menuItem) => {
        menuItem.tabIndex = -1;
      });
    },
    setActiveTarget(el, focus = true) {
      if (!el) return;
      if (!el.tabIndex && this.navDataLoaded) {
        this.setMenuItems();
      }
      el.tabIndex = 0;
      if (!focus) return;
      el.focus();
    },
    setMenuItems() {
      this.$nextTick(() => {
        this.menuItems = Array.from(this.$refs.navigation.querySelectorAll('.nav__menu-item')) || [];
        this.resetTabIndices();
        if (!this.isCompactView) {
          this.setActiveTarget(this.menuItems[0], false);
        }
      });
    },
    updateHeaderHeight() {
      this.setHeaderHeight(this.isCompactView ? 60 : 90);
    },

    scrollMobileNavBugFix(target) {
      if (!this.isCompactView) return;

      if (target.scrollHeight > target.clientHeight) {
        setTimeout(() => {
          target.scrollTo({
            top: 2,
            behavior: 'smooth',
          });
        }, 200);
      }
    },
    toggleMobileNav() {
      if (!this.isMobileNavOpen) {
        this.isMobileNavOpen = true;
        this.isNavOpen = true;
        this.resetTabIndices();
        this.setActiveTarget(this.menuItems[0]);
        this.scrollMobileNavBugFix(this.$refs.navigation.querySelector('.main-nav-container'));
      } else {
        this.isMobileNavOpen = false;
        setTimeout(() => {
          this.closeNav();
        }, 200);
      }
    },

    closeExpandedElements() {
      this.expandedElements.forEach((el, i) => {
        el?.setAttribute('aria-expanded', false);
        this.expandedElements[i] = null;
      });
    },
    closeNav(el) {
      // closes all nav open tiers, updates state so all targets are null
      this.currentTier = 0;
      this.resetTabIndices();

      const focusTarget = el ? this.menuItems.indexOf(el) : 0;

      if (!this.isCompactView) {
        this.setActiveTarget(this.menuItems[focusTarget]);
      } else {
        this.$refs.burger.firstElementChild.focus();
      }

      this.closeExpandedElements();
      this.isNavOpen = false;
    },

    setNestedTabIndex(el) {
      if (el.nextElementSibling.length) {
        this.setActiveTarget(el.nextElementSibling.querySelector('.nav__menu-item'), false);
      }
    },
    focusNewTier(e) {
      const el = e.target;
      if (el.classList.contains('lone')) return;
      // update tabindex for all menu items for keyboard navigation
      this.resetTabIndices();

      // since tier2 is auto-opened, we don't want it to close on re-click
      if (this.currentTier === 2 && this.expandedElements[1] === el) {
        this.setActiveTarget(el);
        return;
      }

      // close any open tier 2
      if (this.currentTier === 2) {
        this.expandedElements[1]?.setAttribute('aria-expanded', false);
        this.expandedElements[1] = null;
      }

      if (el.parentNode.classList.contains('nav-item__tier-0')) {
        this.expandedElements[0]?.setAttribute('aria-expanded', false);
        // check if clicked element is the first expanded element, if it is, close nav, if not expand new
        if (this.expandedElements[0] === el) {
          // clicked tier 1 is already expanded, close nav
          this.closeNav(el);
          return;
        } else {
          // expand new tier 1
          this.currentTier = 1;
          this.setNestedTabIndex(el);
          el.setAttribute('aria-expanded', true);
          this.expandedElements[0] = el;
          this.isNavOpen = true;
          this.scrollMobileNavBugFix(el.nextElementSibling.firstElementChild);

          if (!this.isCompactView && el.nextElementSibling.classList.contains('nav__tier-1')) {
            // if has subnav, expand tier 2
            const firstSubNavElement = el.nextElementSibling.firstElementChild.firstElementChild.firstElementChild;

            this.expandedElements[1] = firstSubNavElement;
            this.currentTier = 2;
            this.setActiveTarget(firstSubNavElement);
            firstSubNavElement.setAttribute('aria-expanded', true);
            return;
          }
        }
      } else {
        // open tier 2
        this.currentTier = 2;
        this.setNestedTabIndex(el);
        el.setAttribute('aria-expanded', true);
        this.scrollMobileNavBugFix(el.nextElementSibling);

        this.expandedElements[1] = el;

        this.isNavOpen = true;
      }
      const newFocusedItem = e.target.nextElementSibling.querySelector('.nav__menu-item');
      this.setActiveTarget(newFocusedItem);
    },

    focusNextMobileMenuItem(e) {
      const mobileMenuItems = Array.from(this.$refs.navigation.querySelectorAll('.nav__mobile-item'));

      const elIndex = mobileMenuItems.indexOf(e.target);
      e.target.tabIndex = -1;
      const targetIndex = elIndex < mobileMenuItems.length - 1 ? elIndex + 1 : 0;
      this.setActiveTarget(mobileMenuItems[targetIndex]);
    },
    focusPreviousMobileMenuItem(e) {
      const mobileMenuItems = Array.from(this.$refs.navigation.querySelectorAll('.nav__mobile-item'));

      const elIndex = mobileMenuItems.indexOf(e.target);
      e.target.tabIndex = -1;
      const targetIndex = elIndex > 0 ? elIndex - 1 : mobileMenuItems.length - 1;
      this.setActiveTarget(mobileMenuItems[targetIndex]);
    },

    focusNextMenuItem(e) {
      let newFocusedItem = null;
      if (this.currentTier === 0) {
        newFocusedItem = !e.target.parentNode.parentNode.nextElementSibling
          ? e.target.parentNode.parentNode.parentNode.querySelector('.nav__menu-item')
          : e.target.parentNode.parentNode.nextElementSibling.firstElementChild.firstElementChild;
      } else {
        newFocusedItem = !e.target.parentNode.nextElementSibling
          ? e.target.parentNode.parentNode.querySelector('.nav__menu-item')
          : e.target.parentNode.nextElementSibling.querySelector('.nav__menu-item');
      }
      e.target.tabIndex = -1;
      this.setActiveTarget(newFocusedItem);
    },

    focusNextElement(e) {
      const elIndex = this.$refs.navItem.indexOf(e.target);
      e.target.tabIndex = -1;
      const targetIndex = elIndex < this.$refs.navItem.length - 1 ? elIndex + 1 : 0;
      this.setActiveTarget(this.$refs.navItem[targetIndex]);
    },

    focusPreviousMenuItem(e) {
      let newFocusedItem = null;
      if (this.currentTier === 0) {
        // have to go a parent up to get the correct next item for b2b on tier 0
        newFocusedItem = !e.target.parentNode.parentNode.previousElementSibling?.querySelector('.nav__menu-item')
          ? e.target.parentNode.parentNode.parentNode.lastElementChild.querySelector('.nav__menu-item')
          : e.target.parentNode.parentNode.previousElementSibling.querySelector('.nav__menu-item');
      } else {
        newFocusedItem = !e.target.parentNode.previousElementSibling?.querySelector('.nav__menu-item')
          ? e.target.parentNode.parentNode.lastElementChild.querySelector('.nav__menu-item')
          : e.target.parentNode.previousElementSibling.querySelector('.nav__menu-item');
      }

      e.target.tabIndex = -1;
      this.setActiveTarget(newFocusedItem);
    },
    focusParentTier() {
      this.expandedElements[this.currentTier] = null;
      this.currentTier = this.currentTier - 1;
      const newFocusedItem = this.expandedElements[this.currentTier];

      // taking shotgun approach here with forEach to accommodate the "Back Button"
      this.resetTabIndices();
      this.setActiveTarget(newFocusedItem);
      newFocusedItem.setAttribute('aria-expanded', false);

      if (this.currentTier === 0) {
        this.expandedElements[this.currentTier] = null;
        this.scrollMobileNavBugFix(newFocusedItem.closest('.main-nav-container'));
        if (!this.isCompactView) {
          this.closeNav(newFocusedItem);
        }
      } else {
        this.scrollMobileNavBugFix(newFocusedItem.parentNode.parentNode);
      }
    },
    closeNavSearch() {
      this.isMobileNavOpen = false;
      setTimeout(
        () => {
          this.currentTier = 0;
          this.menuItems.forEach((menuItem) => {
            menuItem.tabIndex = -1;
          });

          if (!this.isCompactView) {
            this.setActiveTarget(this.menuItems[0], false);
          }

          this.closeExpandedElements();

          this.isNavOpen = false;
        },
        this.isCompactView ? 200 : 0
      );
    },
    handleClose() {
      !this.isCompactView ? this.closeNav(this.expandedElements[0]) : this.toggleMobileNav();
    },
    handleEscape(e) {
      if (e.code === 'Escape' || e.code === 'Tab') {
        this.handleClose();
      }
    },
    handleClickOutside(e) {
      if (e.target.closest('.nav-utility-bar-b2b-container__controls') && this.isCompactView) return;
      const mobileSearchTarget = e.target.id === 'mobile-search-icon' || e.target.closest('#mobile-search-icon');
      const desktopSearchTarget = e.target.closest('.nav__search-container');
      if (mobileSearchTarget || desktopSearchTarget) {
        this.closeNavSearch();
        return;
      }
      if (this.$refs.navWrapper?.contains(e.target) || this.$refs.burger?.firstElementChild?.contains(e.target)) return;
      this.handleClose();
    },

    focusMobileSearchBar() {
      this.navSearchInput.focus();
    },

    closeNavDropdown() {
      this.closeNav(null);
    },
  },
};
</script>

<style lang="scss">
.nav-bar {
  z-index: 9998;
}

.main-nav {
  --nav-translate: 0 0;
  --nav-back-arrow-color: var(--alert);
  --nav-back-button-bg: var(--gray-10);
  --nav-accent-color: var(--alert);
  --nav-mobile-top-bar-bg: var(--gray-100);
  --nav-tier-0-bg: var(--gray-100);
  --nav-tier-0-color: var(--white);
  --nav-tier-1-bg: var(--white);
  --nav-tier-1-bg-active: var(--white);
  --nav-tier-1-color: var(--black);
  --nav-brands-color: var(--white);
  --nav-brands-logos-color: var(--gray-100);
  --nav-brands-logos-bg: var(--white);
  --nav-brands-bg: var(--gray-90);
  --nav-bike-card-bg: var(--gray-02);
  --nav-container: 1024px;
  --nav-focus-offset: -7px;
  --nav-focus-color: var(--blue-100, #0071e3);
  // Search
  --nav-search-border: var(--gray-80);
  --nav-search-bg: var(--gray-100);
  --nav-search-color: var(--white);
  --nav-search-icon-color: var(--nav-search-color);
  --nav-search-placeholder-color: var(--gray-20);

  // Search Focus
  --nav-search-border-focus: var(--gray-80);
  --nav-search-bg-focus: var(--white);
  --nav-search-color-focus: var(--gray-100);
  --nav-search-icon-color-focus: var(--nav-search-color-focus);
  --nav-search-placeholder-color-focus: var(--gray-60);

  grid-template-rows: 60px auto;

  @media (screen(lg)) {
    grid-template-rows: 36px 54px;
  }

  .mb-2 {
    margin-bottom: 1rem;
  }

  a {
    &:hover,
    &:focus {
      color: inherit;
    }
  }

  a,
  button {
    &:focus-visible {
      outline: 2px solid var(--nav-focus-color);
      outline-offset: var(--nav-focus-offset);
    }
  }

  button,
  .nav-logo {
    --nav-focus-offset: 2px;
  }

  @media (prefers-reduced-motion) {
    --nav-reduced-motion: 0s;
  }

  @media (screen(lg)) {
    --nav-tier-1-bg: var(--gray-10);
    --nav-tier-0-bg: var(--gray-100);
    --nav-brands-bg: var(--gray-80);
    --nav-brands-logos-bg: transparent;
    --nav-brands-logos-color: var(--gray-30);
    --nav-brands-color: var(--gray-30);

    .nav-logo {
      --nav-focus-offset: -4px;
    }
  }

  @media (min-width: 1280px) {
    --nav-container: 1280px;
  }

  &.electra {
    --nav-back-button-bg: var(--gray-05);
    --nav-mobile-top-bar-bg: #00adbb;
    --nav-tier-0-bg: var(--white);
    --nav-tier-0-color: var(--gray-100);
    --nav-accent-color: #00adbb;
    --nav-back-arrow-color: currentColor;
    --card-bg-color: #fff;
    --nav-brands-color: var(--gray-100);
    --nav-brands-bg: var(--gray-05);
    --nav-bike-card-bg: var(--white);
    // Search
    --nav-search-border: var(--gray-20);
    --nav-search-bg: var(--gray-05);
    --nav-search-color: var(--gray-90);
    --nav-search-placeholder-color: var(--gray-60);
    --nav-search-icon-color: var(--gray-60);

    // Search Focus
    --nav-search-border-focus: var(--gray-50);
    --nav-search-bg-focus: var(--white);
    --nav-search-color-focus: var(--gray-100);
    --nav-search-icon-color-focus: var(--nav-search-color-focus);

    @media (screen(lg)) {
      --nav-tier-1-bg: var(--gray-05);
      --nav-brands-color: var(--gray-30);
      --nav-brands-bg: var(--gray-80);
    }
  }

  .nav__search-container {
    background: var(--nav-mobile-top-bar-bg);
    transform: translateY(-100%);
    opacity: 0;

    @media (screen(lg)) {
      background: transparent;
      opacity: 1;
      transform: translateY(0);
    }

    &--active {
      opacity: 1;
      transform: translateY(0);
    }
  }
}

.nav__mobile-search-active {
  opacity: 1;
}

.nav-logo {
  .electra {
    width: 146px;

    @media (screen(lg)) {
      width: 148px;
    }
  }
}

.nav-item__tier-0 {
  @media (screen(lg)) {
    > .nav__menu-item {
      transition: translate var(--nav-reduced-motion, 0.2s) ease;

      > span::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -2px;
        height: 2px;
        width: 100%;
        transform-origin: top center;
        transition: scale var(--nav-reduced-motion, 0.2s) ease;
        scale: 1 0;
        background: var(--nav-accent-color);
      }

      @media (hover: hover) {
        &:hover {
          translate: 0 -2px;

          > span::after {
            scale: 1 1;
          }
        }
      }

      &[aria-expanded='true'] {
        translate: 0 -2px;

        > span::after {
          scale: 1 1;
        }
      }
    }
  }
}

.mobile-view {
  --nav-y-translate: -300px;
  --nav-translate: var(--nav-x-translate, 0) var(--nav-y-translate);
  --nav-opacity: 0;

  .flag-icon-us-squared,
  .flag-icon-us-squared::after {
    height: 16px;
    width: 16px;
    line-height: 0;
  }
}

.mobile-open {
  --nav-y-translate: 0;
  --nav-x-translate: 0;
  --nav-opacity: 1;
}

.nav-wrapper {
  translate: var(--nav-translate);
  opacity: var(--nav-opacity);
  transition: translate var(--nav-reduced-motion, 0.2s) ease-in-out, opacity var(--nav-reduced-motion, 0.2s) ease;
  color: var(--nav-tier-0-color);
  background: var(--nav-tier-0-bg);

  @media (screen(lg)) {
    background: var(--nav-tier-0-bg);
  }
}

.nav__mobile-item > .pdl-icon {
  @media (screen(lg)) {
    display: none;
  }
}

.active--tier-0 {
  --nav-x-translate: -100%;

  @media (screen(lg)) {
    --nav-translate: 0 0;
  }
}

.active--tier-1 {
  --nav-x-translate: -200%;

  @media (screen(lg)) {
    --nav-translate: 0 0;
  }
}

.nav-bg-overlay {
  // prettier-ignore
  cursor: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' stroke='rgb(255,255,255)' stroke-width='2' viewBox='0 0 24 24'%3e %3cpath  d='m3.5 3.5 13 13M16.5 3.5l-13 13'/%3e %3c/svg%3e") 12 0, auto;
  background-color: #0a0a0a80;
  backdrop-filter: blur(24px);
}

.burger {
  --burger-transition: translate var(--nav-reduced-motion, 0.22s) ease-in,
    rotate var(--nav-reduced-motion, 0.22s) ease-in-out,
    opacity var(--nav-reduced-motion, 0.1s) ease var(--nav-reduced-motion, 0.1s);

  position: relative;
  display: block;
  width: 18px;
  height: 2px;
  background: currentColor;
  rotate: var(--burger-rotate);
  transition: var(--burger-transition);

  &::before,
  &::after {
    content: '';
    position: absolute;
    background: currentColor;
    height: 100%;
    width: 100%;
    left: 0;
    translate: 0 var(--burger-y);
    rotate: var(--burger-rotate);
    transition: var(--burger-transition);
  }

  &::before {
    --burger-y: -5px;
  }

  &::after {
    --burger-y: 5px;
  }

  .active & {
    --burger-rotate: 135deg;

    &::before {
      --burger-y: 0;
      --burger-rotate: 0;

      opacity: 0;
    }

    &::after {
      --burger-y: 0;
      --burger-rotate: 90deg;
    }
  }
}

.nav-section-title {
  padding-left: 0.5rem;

  &::before {
    content: '';
    display: inline-block;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 0.125rem;
    background: var(--nav-accent-color, var(--alert));
  }
}

.nav-card {
  background-color: var(--nav-bike-card-bg);
}
</style>
